import React, { useState } from "react";
import { useFormikContext } from "formik";
import { getEnvCollection } from "components/FormerEditor/common";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { FEATURE_GROUP_LIST_DESTINATIONS } from "utils/drag";
import { useSelector } from "react-redux";
import TableHeader from "./TableHeader";
import TableGroupRow from "./TableGroupRow";
import FeatureForm from "../FeatureForm";
import {
  addFeatureAndAddToFeatureGroup,
  updateFeatureGroupTableFeatures
} from "utils/feature";
import Select from "components/Form/fields/Select";
import { DRAG_FEATURE, ADD_FEATURE, SELECT_PRODUCT } from "utils/analytics";
import Analytics from "utils/analytics";

const TABLE_STYLE = {
  borderCollapse: "separate",
  borderSpacing: 0,
  position: "relative"
};

const TABLE_BODY_STYLE = {
  display: "table-row-group",
  verticalAlign: "middle"
};

const DEFAULT_HEADER_WIDTH = 30;

const FeatureGroupsPreviewTable = ({ featureGroups, products, tracking }) => {
  const { values, setValues, setTouched } = useFormikContext();

  const selectorProducts = useSelector((state) =>
    getEnvCollection(state.getIn(["product", "data"]).toJS())
  );
  const ctxProducts = products || selectorProducts;

  const tableProducts = values.productLayout.reduce((memo, { uid }) => {
    const productMatch = ctxProducts.find(({ id }) => uid === id);
    if (productMatch) {
      memo.push(productMatch);
    }
    return memo;
  }, []);

  const [activeIndex, setActiveIndex] = useState(0);

  const selectProps = {
    options: tableProducts.map(({ name }, prodIx) => ({
      uid: prodIx,
      label: name
    })),
    value: activeIndex,
    disabled: tableProducts.length <= 1,
    onChange: () => (evt) => {
      const selection = evt.currentTarget.value;
      Analytics.track(SELECT_PRODUCT, {
        ...tracking,
        product: selection
      });
      setActiveIndex(parseInt(selection));
    },
    customClasses: {
      input: "border-box input-reset ba b--black-20 pa2 mb0 db w-100 pv2 f5 br2"
    }
  };

  const thWidth = DEFAULT_HEADER_WIDTH;
  const tdWidth = (100 - thWidth) / tableProducts.length;

  return (
    <DragDropContext
      onDragEnd={updateFeatureGroupTableFeatures({
        values,
        setValues,
        setTouched,
        tracking: {
          event: DRAG_FEATURE,
          data: {
            ...tracking
          }
        }
      })}
    >
      <div className="w-100 flex flex-column">
        <div className="dib dn-ns f5 w-100 pb4 pb3-ns">
          <div className="f5 fw5 pb2">Active product</div>
          <Select {...selectProps} />
        </div>
        {/* Treat every group as a table */}
        {featureGroups.map((featureGroup, featureGroupIx) => {
          const isProductsControlGroup = featureGroupIx === 0;
          return (
            <Droppable
              key={featureGroupIx}
              droppableId={`${FEATURE_GROUP_LIST_DESTINATIONS.FEATURE_GROUP}.${featureGroup.uuid}`}
            >
              {(provided, snapshot) => {
                return (
                  <table
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="w-100 f5"
                    cellSpacing="0"
                    style={TABLE_STYLE}
                  >
                    {isProductsControlGroup && (
                      <TableHeader
                        products={tableProducts}
                        activeIndex={activeIndex}
                      />
                    )}
                    <tbody className="lh-copy" style={TABLE_BODY_STYLE}>
                      <TableGroupRow
                        tracking={tracking}
                        index={featureGroupIx}
                        key={`groupHeader-${featureGroupIx}`}
                        products={tableProducts}
                        activeIndex={activeIndex}
                        featureGroup={featureGroup}
                        features={values.features}
                        thWidth={thWidth}
                        tdWidth={tdWidth}
                      />
                      {!snapshot.isDraggingOver && (
                        <tr>
                          <td
                            colSpan={tableProducts.length + 1}
                            className="bl br hairline-1 bb br--bottom br2"
                          >
                            <div className="ph3 pb3 pt2 bg-content--secondary">
                              <FeatureForm
                                placeholder={`Add feature to ${featureGroup.title}`}
                                customClasses={{ title: "f5" }}
                                addFeatures={addFeatureAndAddToFeatureGroup({
                                  featureGroup,
                                  values,
                                  products: tableProducts,
                                  setValues,
                                  setTouched,
                                  tracking: {
                                    event: ADD_FEATURE,
                                    data: {
                                      ...tracking
                                    }
                                  }
                                })}
                                copy={{ title: `` }}
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                      {snapshot.isDraggingOver && (
                        <tr>
                          <td
                            colSpan={tableProducts.length + 1}
                            className="bl br hairline-1 bb br--bottom br2"
                          >
                            <div
                              style={{ height: 54 }}
                              className="bg-white"
                            ></div>
                          </td>
                        </tr>
                      )}
                      {provided.placeholder}
                    </tbody>
                  </table>
                );
              }}
            </Droppable>
          );
        })}
      </div>
    </DragDropContext>
  );
};

export default FeatureGroupsPreviewTable;
